/**
 * RoleGuard — Wrapper component that renders children only if the
 * authenticated user's role is in the allowedRoles list.
 * issue_139: Frontend role-based access control.
 *
 * Usage:
 *   <RoleGuard allowedRoles={[UserRole.USER, UserRole.ADMIN]}>
 *     <Button>Crea Deck</Button>
 *   </RoleGuard>
 *
 *   <RoleGuard allowedRoles={[UserRole.ADMIN]} fallback={<AccessDenied />}>
 *     <AdminPanel />
 *   </RoleGuard>
 */

import { type ReactNode } from 'react';
import { UserRole } from '@flashcard/shared';
import { useAuth } from './auth.hook';

interface RoleGuardProps {
  allowedRoles: UserRole[];
  fallback?: ReactNode;
  children: ReactNode;
}

export function RoleGuard({ allowedRoles, fallback = null, children }: RoleGuardProps) {
  const { user } = useAuth();

  if (!user || !allowedRoles.includes(user.role as UserRole)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

export default RoleGuard;